import { useEffect, useCallback } from 'react';
import { useWeighing } from '@/contexts/WeighingContext';

interface UseWeighingRefreshOptions {
  weighingId?: string;
  autoRefresh?: boolean;
  refreshInterval?: number; // en millisecondes
  onRefresh?: (weighing: any) => void;
  onError?: (error: string) => void;
}

export const useWeighingRefresh = (options: UseWeighingRefreshOptions = {}) => {
  const {
    weighingId,
    autoRefresh = false,
    refreshInterval = 30000, // 30 secondes par défaut
    onRefresh,
    onError
  } = options;

  const { state, refreshWeighing, setError } = useWeighing();

  // Fonction pour recharger manuellement
  const manualRefresh = useCallback(async () => {
    if (!weighingId) return;
    
    try {
      await refreshWeighing(weighingId);
      if (onRefresh && state.currentWeighing) {
        onRefresh(state.currentWeighing);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors du rechargement';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    }
  }, [weighingId, refreshWeighing, state.currentWeighing, onRefresh, onError, setError]);

  // Rechargement automatique
  useEffect(() => {
    if (!autoRefresh || !weighingId) return;

    const interval = setInterval(() => {
      manualRefresh();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, weighingId, refreshInterval, manualRefresh]);

  // Rechargement initial
  useEffect(() => {
    if (weighingId && !state.currentWeighing) {
      manualRefresh();
    }
  }, [weighingId, state.currentWeighing, manualRefresh]);

  return {
    manualRefresh,
    isLoading: state.isLoading,
    error: state.error,
    currentWeighing: state.currentWeighing
  };
};

export default useWeighingRefresh;
